import { type RefObject, useEffect, useLayoutEffect } from 'react'

import { useHeaderStore } from '@/store/header.store'

// The app header floats over its screen: publish its height on the parent (the
// screen root, see [data-app-hdr] in index.css) so scrollers pad under it, and
// raise it once a scroller that starts under it moves off the top.
export function useFloatingHeader(
  ref: RefObject<HTMLElement | null>,
  slot: '--app-hdr-m' | '--app-hdr-d',
  enabled: boolean,
) {
  const setRaised = useHeaderStore((s) => s.setRaised)

  useLayoutEffect(() => {
    const el = ref.current
    const host = el?.parentElement
    if (!enabled || !el || !host) return
    host.setAttribute('data-app-hdr', '')
    const measure = () => host.style.setProperty(slot, `${el.getBoundingClientRect().height}px`)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => {
      ro.disconnect()
      host.style.removeProperty(slot)
      if (
        !host.style.getPropertyValue('--app-hdr-m') &&
        !host.style.getPropertyValue('--app-hdr-d')
      )
        host.removeAttribute('data-app-hdr')
    }
  }, [ref, slot, enabled])

  useEffect(() => {
    const host = ref.current?.parentElement
    if (!enabled || !host) return
    const lastY = new WeakMap<EventTarget, number>()
    const onScroll = (e: Event) => {
      const el = e.target
      if (!(el instanceof HTMLElement)) return
      const y = el.scrollTop
      // Horizontal scrolls (tab swipers, chip rows) leave scrollTop alone.
      if ((lastY.get(el) ?? 0) === y) return
      lastY.set(el, y)
      // Only scrollers whose top edge is under the header can slide content beneath it.
      if (el.getBoundingClientRect().top - host.getBoundingClientRect().top > 1) return
      setRaised(y > 0)
    }
    host.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => {
      host.removeEventListener('scroll', onScroll, { capture: true })
      setRaised(false)
    }
  }, [ref, enabled, setRaised])
}
